import React, { useState, useEffect, useCallback } from 'react';
import { CognitoUserPool } from 'amazon-cognito-identity-js';
import { Shield, ShieldCheck, ShieldOff, Copy, Check } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import config from '../config';

const userPool = new CognitoUserPool({
  UserPoolId: config.userPoolId,
  ClientId: config.userPoolWebClientId
});

/**
 * Security settings: enroll / disable an authenticator app (TOTP MFA).
 * Enrollment: associateSoftwareToken -> add key to authenticator ->
 * verifySoftwareToken(code) -> setUserMfaPreference(preferred).
 */
const SecurityPage = () => {
  const [status, setStatus] = useState('loading'); // loading | off | on | enrolling
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [working, setWorking] = useState(false);
  const [copied, setCopied] = useState(false);

  const getSessionUser = useCallback(() => new Promise((resolve, reject) => {
    const user = userPool.getCurrentUser();
    if (!user) return reject(new Error('Not signed in'));
    user.getSession((err, session) => {
      if (err || !session?.isValid()) return reject(err || new Error('Session expired'));
      resolve(user);
    });
  }), []);

  const refreshStatus = useCallback(async () => {
    try {
      const user = await getSessionUser();
      user.getUserData((err, data) => {
        if (err) { setError(err.message); setStatus('off'); return; }
        const enabled = (data?.UserMFASettingList || []).includes('SOFTWARE_TOKEN_MFA');
        setStatus(enabled ? 'on' : 'off');
      }, { bypassCache: true });
    } catch (err) {
      setError(err.message);
      setStatus('off');
    }
  }, [getSessionUser]);

  useEffect(() => { refreshStatus(); }, [refreshStatus]);

  const beginEnrollment = async () => {
    setError(''); setMessage(''); setWorking(true);
    try {
      const user = await getSessionUser();
      user.associateSoftwareToken({
        associateSecretCode: (secretCode) => {
          setSecret(secretCode);
          setStatus('enrolling');
          setWorking(false);
        },
        onFailure: (err) => { setError(err.message || 'Could not start enrollment'); setWorking(false); }
      });
    } catch (err) { setError(err.message); setWorking(false); }
  };

  const confirmEnrollment = async (e) => {
    e.preventDefault();
    setError(''); setWorking(true);
    try {
      const user = await getSessionUser();
      user.verifySoftwareToken(code.trim(), 'BlackCapHealth Console', {
        onSuccess: () => {
          user.setUserMfaPreference(null, { PreferredMfa: true, Enabled: true }, (err) => {
            setWorking(false);
            if (err) { setError(err.message); return; }
            setMessage('Two-factor authentication is on. You will be asked for a code at every sign-in.');
            setSecret(''); setCode('');
            refreshStatus();
          });
        },
        onFailure: (err) => { setError(err.message || 'Code did not match - try again'); setWorking(false); }
      });
    } catch (err) { setError(err.message); setWorking(false); }
  };

  const disableMfa = async () => {
    setError(''); setMessage(''); setWorking(true);
    try {
      const user = await getSessionUser();
      user.setUserMfaPreference(null, { PreferredMfa: false, Enabled: false }, (err) => {
        setWorking(false);
        if (err) { setError(err.message); return; }
        setMessage('Two-factor authentication is off.');
        refreshStatus();
      });
    } catch (err) { setError(err.message); setWorking(false); }
  };

  const otpauthUri = secret
    ? `otpauth://totp/BlackCapHealth:${encodeURIComponent(userPool.getCurrentUser()?.getUsername() || 'user')}?secret=${secret}&issuer=BlackCapHealth`
    : '';

  const copySecret = () => {
    navigator.clipboard?.writeText(secret).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="min-h-screen bg-porcelain py-8">
      <div className="max-w-2xl mx-auto px-4">
        <h1 className="text-2xl font-bold text-ink flex items-center gap-2 mb-1">
          <Shield className="text-copper-500" size={22} /> Security
        </h1>
        <p className="text-sm text-ink-soft mb-6">Protect your account with an authenticator app (Google Authenticator, Authy, 1Password, etc.)</p>

        {message && <div className="mb-4 p-3 rounded-lg bg-spruce-50 border border-spruce-200 text-spruce-800 text-sm">{message}</div>}
        {error && <div className="mb-4 p-3 rounded-lg bg-red-50 border border-danger text-danger text-sm">{error}</div>}

        <div className="bg-white border border-line rounded-xl p-6">
          {status === 'loading' && <div className="text-gray-500">Checking status…</div>}

          {status === 'on' && (
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <ShieldCheck className="text-pos mt-0.5" size={22} />
                <div>
                  <div className="font-semibold text-ink">Two-factor authentication is on</div>
                  <div className="text-sm text-ink-soft mt-1">A 6-digit code from your authenticator app is required at every sign-in.</div>
                </div>
              </div>
              <button onClick={disableMfa} disabled={working}
                className="border border-danger text-danger px-4 py-2 rounded-lg text-sm font-semibold hover:bg-red-50 disabled:opacity-50 whitespace-nowrap">
                Turn off
              </button>
            </div>
          )}

          {status === 'off' && (
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <ShieldOff className="text-warn mt-0.5" size={22} />
                <div>
                  <div className="font-semibold text-ink">Two-factor authentication is off</div>
                  <div className="text-sm text-ink-soft mt-1">Strongly recommended for anyone with access to patient data.</div>
                </div>
              </div>
              <button onClick={beginEnrollment} disabled={working}
                className="bg-spruce-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-spruce-700 disabled:opacity-50 whitespace-nowrap">
                Set up
              </button>
            </div>
          )}

          {status === 'enrolling' && (
            <div>
              <div className="font-semibold text-ink mb-2">1. Scan with your authenticator app</div>
              <p className="text-sm text-ink-soft mb-4">
                Open your authenticator app, choose <strong>Add account → Scan QR code</strong>, and point it here:
              </p>
              <div className="flex justify-center mb-4">
                <div className="bg-white border border-line rounded-xl p-4">
                  <QRCodeSVG value={otpauthUri} size={192} level="M" />
                </div>
              </div>
              <details className="mb-5">
                <summary className="text-sm text-spruce-600 cursor-pointer hover:text-spruce-800">Can't scan? Enter the key manually</summary>
                <div className="flex items-center gap-2 bg-mist border border-line rounded-lg px-4 py-3 mt-2">
                  <code className="font-data text-xs break-all flex-1">{secret}</code>
                  <button onClick={copySecret} className="p-2 text-spruce-600 hover:bg-spruce-50 rounded" title="Copy key">
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1.5">Account: BlackCapHealth Console · Type: time-based</p>
              </details>

              <form onSubmit={confirmEnrollment}>
                <div className="font-semibold text-ink mb-2">2. Enter the 6-digit code the app shows</div>
                <div className="flex gap-3">
                  <input
                    type="text" inputMode="numeric" maxLength={6} autoFocus
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    className="flex-1 text-center text-xl tracking-[0.4em] font-data px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500 focus:border-transparent"
                    placeholder="000000"
                  />
                  <button type="submit" disabled={working || code.length !== 6}
                    className="bg-spruce-600 text-white px-5 py-2.5 rounded-lg font-semibold hover:bg-spruce-700 disabled:opacity-50">
                    {working ? 'Verifying…' : 'Verify & enable'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>

        <p className="text-xs text-gray-400 mt-4">
          If you lose your authenticator device, an administrator can reset MFA for your account from the Cognito console.
        </p>
      </div>
    </div>
  );
};

export default SecurityPage;
